import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "@/lib/toast";
import api from "@/lib/api";
import { PageHeader, Card, Field, Input, Textarea, PrimaryButton } from "@/components/admin/AdminUI";
import { CloudinaryUploader } from "@/components/CloudinaryUploader";
import MDEditor from "@uiw/react-md-editor";
import { X, Instagram, Youtube, Facebook, ArrowUp, ArrowDown, Plus } from "lucide-react";

// Substack & TikTok aren't in lucide-react. Inline single-path SVGs keep the
// dropdown previews consistent and brand-recognisable.
const TikTokIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={props.className} aria-hidden="true">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V9.36a8.16 8.16 0 0 0 4.77 1.52V7.42a4.85 4.85 0 0 1-1.84-.73Z" />
  </svg>
);
const SubstackIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={props.className} aria-hidden="true">
    <path d="M22.539 8.242H1.46V5.406h21.08v2.836ZM1.46 10.812V24L12 18.11 22.54 24V10.812H1.46ZM22.54 0H1.46v2.836h21.08V0Z" />
  </svg>
);

const SOCIAL_PLATFORMS = [
  { key: "instagram", label: "Instagram", Icon: Instagram },
  { key: "youtube", label: "YouTube", Icon: Youtube },
  { key: "substack", label: "Substack", Icon: SubstackIcon },
  { key: "facebook", label: "Facebook", Icon: Facebook },
  { key: "tiktok", label: "TikTok", Icon: TikTokIcon },
];

const AD_SLOTS = [
  { key: "home_mid", label: "Home — middle" },
  { key: "home_lower", label: "Home — lower" },
  { key: "brands_mid", label: "Brands list — below cards" },
  { key: "brand_detail", label: "Brand detail — before related items" },
  { key: "clothing_inline", label: "Clothing list — inline" },
  { key: "clothing_detail", label: "Clothing detail — before related items" },
  { key: "materials_mid", label: "Materials Guide — between sections" },
  { key: "materials_certifications_mid", label: "Materials certifications — below cards" },
  { key: "brand_certifications_mid", label: "Brand certifications — below cards" },
  { key: "cms_inline", label: "CMS pages — after content" },
];

function normalizeSlot(v) {
  // Back-compat: legacy bool → {enabled, slot_id:""}
  if (v && typeof v === "object") return { enabled: !!v.enabled, slot_id: v.slot_id || "" };
  return { enabled: !!v, slot_id: "" };
}

export default function AdminSettings() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["admin-settings"], queryFn: async () => (await api.get("/admin/settings")).data });
  const [form, setForm] = useState({});
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  useEffect(() => { if (data) setForm(data); }, [data]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.type === "checkbox" ? e.target.checked : (e.target ? e.target.value : e) }));
  const setAdEnabled = (slot) => (e) => setForm((f) => ({
    ...f,
    ad_slots: {
      ...(f.ad_slots || {}),
      [slot]: { ...normalizeSlot(f.ad_slots?.[slot]), enabled: e.target.checked },
    },
  }));
  const setAdSlotId = (slot) => (e) => setForm((f) => ({
    ...f,
    ad_slots: {
      ...(f.ad_slots || {}),
      [slot]: { ...normalizeSlot(f.ad_slots?.[slot]), slot_id: e.target.value.trim() },
    },
  }));

  // ---------- Social links helpers ----------
  const links = Array.isArray(form.social_links) ? form.social_links : [];
  const setLinks = (next) => setForm((f) => ({ ...f, social_links: next }));
  const addLink = () => {
    // Default new row to the first unused platform if any, else Instagram.
    const used = new Set(links.map((l) => l.platform));
    const next = SOCIAL_PLATFORMS.find((p) => !used.has(p.key))?.key || SOCIAL_PLATFORMS[0].key;
    setLinks([...links, { platform: next, url: "" }]);
  };
  const updateLink = (i, patch) => setLinks(links.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const removeLink = (i) => setLinks(links.filter((_, idx) => idx !== i));
  const moveLink = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= links.length) return;
    const copy = links.slice();
    [copy[i], copy[j]] = [copy[j], copy[i]];
    setLinks(copy);
  };

  async function save() {
    try {
      await api.put("/admin/settings", form);
      toast.success("Settings saved");
      qc.invalidateQueries({ queryKey: ["admin-settings"] });
      qc.invalidateQueries({ queryKey: ["public-settings"] });
    } catch (e) { toast.error("Save failed"); }
  }

  async function changePw() {
    if (!pw.current_password || !pw.new_password) return;
    try {
      await api.post("/admin/change-password", pw);
      toast.success("Password changed");
      setPw({ current_password: "", new_password: "" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }

  const [tab, setTab] = useState("content"); // 'content' | 'adsense' | 'admin'

  return (
    <div data-color-mode="light">
      <PageHeader title="Settings" actions={<PrimaryButton onClick={save} data-testid="settings-save-button">Save</PrimaryButton>} />

      {/* Tabs */}
      <div className="flex gap-1 mb-5 border-b border-[#E9ECEF]" data-testid="settings-tabs">
        <SettingsTab active={tab === "content"} onClick={() => setTab("content")} testid="settings-tab-content">Home page &amp; footer</SettingsTab>
        <SettingsTab active={tab === "adsense"} onClick={() => setTab("adsense")} testid="settings-tab-adsense">AdSense</SettingsTab>
        <SettingsTab active={tab === "admin"} onClick={() => setTab("admin")} testid="settings-tab-admin">Crawler &amp; admin</SettingsTab>
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        {tab === "content" && (
        <Card className="space-y-4 lg:col-span-2">
          <h3 className="font-semibold">Site</h3>
          <Field
            label="Hero eyebrow"
            hint="Small uppercase text shown above the hero headline."
          ><Input value={form.hero_eyebrow || ""} onChange={set("hero_eyebrow")} /></Field>
          <Field
            label="Hero headline (Tagline)"
            hint="Home page hero. Wrap a word in *asterisks* to style it italic + green (e.g. Find clothing that actually meets *sustainable* material standards.)."
          ><Input value={form.tagline || ""} onChange={set("tagline")} /></Field>
          <Field
            label="Hero subtitle"
            hint="Paragraph shown below the hero headline."
          ><Textarea rows={3} value={form.hero_subtitle || ""} onChange={set("hero_subtitle")} /></Field>
          <Field
            label="Hero image"
            hint="Upload a file or paste a URL — both go through the same uploader (use the 'Web Address' tab inside the widget for URLs)."
          >
            <div className="flex items-center gap-3">
              <Input value={form.hero_image_url || ""} onChange={set("hero_image_url")} placeholder="https://…" data-testid="hero-image-url-input" />
              <CloudinaryUploader
                mode="admin"
                folder="sustainably_curated/hero"
                multiple={false}
                label={form.hero_image_url ? "Replace" : "Upload"}
                onUploaded={(url) => setForm((f) => ({ ...f, hero_image_url: url }))}
              />
            </div>
            {form.hero_image_url && (
              <div className="mt-3 relative inline-block">
                <img src={form.hero_image_url} alt="Hero" className="h-28 w-auto rounded-md border border-[#E9ECEF] object-cover" />
                <button
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, hero_image_url: "" }))}
                  className="absolute -top-2 -right-2 bg-white border border-[#E9ECEF] rounded-full p-1 shadow hover:bg-red-50 hover:border-red-200"
                  data-testid="hero-image-clear"
                  aria-label="Remove hero image"
                >
                  <X className="w-3.5 h-3.5 text-red-600" />
                </button>
              </div>
            )}
          </Field>

          <Field
            label="Default social/OG image (1200×630)"
            hint={<>Used as the social preview when a link to any public page is shared (Facebook, X/Twitter, LinkedIn, WhatsApp, iMessage…). Falls back to the hero image when empty. Per-page overrides live in <code>/admin/seo</code>.</>}
          >
            <div className="flex items-center gap-3">
              <Input
                value={form.default_og_image_url || ""}
                onChange={set("default_og_image_url")}
                placeholder="https://res.cloudinary.com/…/og-card-1200x630.jpg"
                data-testid="default-og-image-url-input"
              />
              <CloudinaryUploader
                mode="admin"
                folder="sustainably_curated/og"
                multiple={false}
                label={form.default_og_image_url ? "Replace" : "Upload"}
                onUploaded={(url) => setForm((f) => ({ ...f, default_og_image_url: url }))}
              />
            </div>
            {form.default_og_image_url && (
              <div className="mt-3 relative inline-block">
                <img
                  src={form.default_og_image_url}
                  alt="Default OG card"
                  className="h-28 w-auto rounded-md border border-[#E9ECEF] object-cover"
                  style={{ aspectRatio: "1200/630" }}
                />
                <button
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, default_og_image_url: "" }))}
                  className="absolute -top-2 -right-2 bg-white border border-[#E9ECEF] rounded-full p-1 shadow hover:bg-red-50 hover:border-red-200"
                  data-testid="default-og-image-clear"
                  aria-label="Remove default OG image"
                >
                  <X className="w-3.5 h-3.5 text-red-600" />
                </button>
              </div>
            )}
          </Field>
        </Card>
        )}

        {tab === "content" && (
        <Card className="space-y-4 lg:col-span-2">
          <h3 className="font-semibold">Home page promise pillars</h3>          <p className="text-xs text-[#6C757D] -mt-2">The three callouts shown right below the hero (icons stay fixed: leaf, warning, x-circle). Bodies support full Markdown — bold, italic, links, lists, code.</p>
          <div className="grid md:grid-cols-3 gap-4">
            {[1, 2, 3].map((n) => (
              <div key={n} className="space-y-3 border border-[#E9ECEF] rounded-md p-3">
                <div className="text-xs uppercase tracking-wider text-[#6C757D]">Pillar {n}</div>
                <Field label="Title">
                  <Input
                    value={form[`pillar_${n}_title`] || ""}
                    onChange={set(`pillar_${n}_title`)}
                    data-testid={`pillar-${n}-title-input`}
                  />
                </Field>
                <Field label="Body (Markdown)">
                  <div className="border border-[#E9ECEF] rounded-md overflow-hidden">
                    <MDEditor
                      value={form[`pillar_${n}_body`] || ""}
                      onChange={(v) => setForm((f) => ({ ...f, [`pillar_${n}_body`]: v || "" }))}
                      height={220}
                      preview="edit"
                      data-testid={`pillar-${n}-body-editor`}
                    />
                  </div>
                </Field>
              </div>
            ))}
          </div>
          <Field
            label="Pillars footer line (Markdown)"
            hint="A single editorial line shown centered below the three pillars on the home page. Renders in the dark-green editorial accent. Supports bold, italic, and links (e.g. `Read more in our [Materials Guide](/materials-guide).`)."
          >
            <div className="border border-[#E9ECEF] rounded-md overflow-hidden">
              <MDEditor
                value={form.pillars_footer || ""}
                onChange={(v) => setForm((f) => ({ ...f, pillars_footer: v || "" }))}
                height={140}
                preview="edit"
                data-testid="pillars-footer-editor"
              />
            </div>
          </Field>
        </Card>
        )}

        {tab === "adsense" && (
        <Card className="space-y-4 lg:col-span-2">
          <h3 className="font-semibold">AdSense</h3>
          <div className="flex items-center justify-between border border-[#E9ECEF] rounded-md px-3 py-2">
            <div>
              <div className="text-sm font-medium">Ads enabled</div>
              <div className="text-xs text-[#6C757D]">Master switch. When off, no ads load anywhere on the public site.</div>
            </div>
            <input
              type="checkbox"
              checked={form.ads_enabled !== false}
              onChange={set("ads_enabled")}
              data-testid="ads-enabled-toggle"
            />
          </div>
          <div className="text-xs text-[#6C757D] bg-[#F8F9FA] border border-[#E9ECEF] rounded p-2 leading-relaxed">
            <strong>One-time setup:</strong> in your AdSense dashboard, enable <em>Privacy &amp; messaging → Funding Choices</em> and publish a consent message. The CMP loads automatically. ads.txt is served at <code>/ads.txt</code>.
          </div>
          <div className="space-y-3">
            {AD_SLOTS.map(({ key, label }) => {
              const slot = normalizeSlot(form.ad_slots?.[key]);
              return (
                <div key={key} className="border border-[#E9ECEF] rounded-md p-3">
                  <label className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">{label}</span>
                    <input
                      type="checkbox"
                      checked={slot.enabled}
                      onChange={setAdEnabled(key)}
                      data-testid={`ad-slot-${key}-enabled`}
                    />
                  </label>
                  {slot.enabled && (
                    <Input
                      placeholder="Numeric ad-unit slot ID (e.g. 1234567890)"
                      value={slot.slot_id}
                      onChange={setAdSlotId(key)}
                      data-testid={`ad-slot-${key}-id`}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </Card>
        )}

        {tab === "admin" && (
        <Card className="space-y-4">
          <h3 className="font-semibold">Crawler</h3>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Concurrency"><Input type="number" value={form.crawler_concurrency || 3} onChange={set("crawler_concurrency")} /></Field>
            <Field label="Product cap per run"><Input type="number" value={form.crawler_product_cap || 40} onChange={set("crawler_product_cap")} /></Field>
            <Field label="LLM token cap (per page)"><Input type="number" value={form.llm_token_cap || 12000} onChange={set("llm_token_cap")} /></Field>
            <Field label="Featured items on Home"><Input type="number" value={form.featured_items_count || 10} onChange={set("featured_items_count")} /></Field>
          </div>
          <Field
            label="ScraperAPI key"
            hint="Used by the 'Paid' crawler button as a fallback when the free crawler is blocked. Get a key at scraperapi.com. Stored in MongoDB so it survives redeploys without needing to edit the production environment."
          >
            <Input
              value={form.scraperapi_key || ""}
              onChange={set("scraperapi_key")}
              placeholder="Paste your ScraperAPI key"
              autoComplete="off"
              spellCheck={false}
              data-testid="scraperapi-key-input"
            />
            <p className="text-xs text-[#6C757D] mt-1">
              {form.scraperapi_key
                ? <span className="text-forest font-medium">Configured.</span>
                : <span className="text-red-600 font-medium">Not configured.</span>}
              {" "}Click <em>Save</em> after editing. Falls back to the <code>SCRAPERAPI_KEY</code> env var when this field is empty.
            </p>
          </Field>
          <div className="flex items-center justify-between border-t border-[#E9ECEF] pt-4" data-testid="show-non-approved-row">
            <div>
              <div className="text-sm font-medium">Show non-approved clothing on public list</div>
              <p className="text-xs text-[#6C757D] mt-0.5">When on, pending and not-yet-reviewed items also appear in the public clothing list.</p>
            </div>
            <input type="checkbox" checked={!!form.show_non_approved_clothing} onChange={set("show_non_approved_clothing")} data-testid="show-non-approved-toggle" />
          </div>

          <div className="border-t border-[#E9ECEF] pt-4">
            <h3 className="font-semibold mb-3">GDPR — contact form retention</h3>
            <Field
              label="Auto-delete contact submissions after (months)"
              hint="Daily background job purges submissions + their Cloudinary attachments older than this. Set to 0 to disable auto-purge. Manual GDPR actions still work."
            >
              <Input
                type="number"
                min="0"
                max="120"
                value={form.contact_retention_months ?? 24}
                onChange={set("contact_retention_months")}
                data-testid="contact-retention-input"
              />
            </Field>
          </div>
        </Card>
        )}

        {tab === "content" && (
        <Card className="space-y-4 lg:col-span-2">
          <h3 className="font-semibold">Footer</h3>
          <Field
            label='Message under "Sustainably Curated"'
            hint="Shown in the left column of the public footer, right below the brand name."
          >
            <Textarea
              rows={3}
              value={form.footer_about || ""}
              onChange={set("footer_about")}
              data-testid="footer-about-input"
            />
          </Field>

          <div className="border-t border-[#E9ECEF] pt-4">
            <div className="flex items-center justify-between mb-1">
              <h4 className="text-sm font-semibold">Follow the Creator</h4>
              <button
                type="button"
                onClick={addLink}
                className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 border border-forest text-forest hover:bg-forest hover:text-ivory transition-colors"
                data-testid="social-add-button"
              >
                <Plus className="w-3.5 h-3.5" /> Add link
              </button>
            </div>
            <p className="text-xs text-[#6C757D] mb-3">
              Choose a platform, paste the profile URL, and drag the arrows to reorder. The links render as icons in the right column of the public footer. An empty list hides the section entirely.
            </p>

            {links.length === 0 ? (
              <div className="text-xs text-[#6C757D] border border-dashed border-[#E9ECEF] rounded-md p-4 text-center">
                No social links yet. Click <em>Add link</em> to add one.
              </div>
            ) : (
              <ul className="space-y-2" data-testid="social-links-list">
                {links.map((link, i) => {
                  const def = SOCIAL_PLATFORMS.find((p) => p.key === link.platform) || SOCIAL_PLATFORMS[0];
                  const Icon = def.Icon;
                  return (
                    <li
                      key={i}
                      className="grid grid-cols-[auto_140px_1fr_auto] gap-2 items-center border border-[#E9ECEF] rounded-md px-2 py-2 bg-white"
                      data-testid={`social-link-row-${i}`}
                    >
                      <div className="w-8 h-8 grid place-items-center bg-[#F8F9FA] text-forest" aria-hidden="true">
                        <Icon className="w-4 h-4" />
                      </div>
                      <select
                        value={link.platform}
                        onChange={(e) => updateLink(i, { platform: e.target.value })}
                        className="text-sm border border-[#E9ECEF] rounded-md px-2 py-1.5 bg-white"
                        data-testid={`social-platform-${i}`}
                      >
                        {SOCIAL_PLATFORMS.map((p) => (
                          <option key={p.key} value={p.key}>{p.label}</option>
                        ))}
                      </select>
                      <Input
                        value={link.url}
                        onChange={(e) => updateLink(i, { url: e.target.value })}
                        placeholder="https://…"
                        data-testid={`social-url-${i}`}
                      />
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => moveLink(i, -1)}
                          disabled={i === 0}
                          className="p-1.5 text-[#6C757D] hover:text-forest disabled:opacity-30 disabled:cursor-not-allowed"
                          title="Move up"
                          data-testid={`social-up-${i}`}
                        >
                          <ArrowUp className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => moveLink(i, 1)}
                          disabled={i === links.length - 1}
                          className="p-1.5 text-[#6C757D] hover:text-forest disabled:opacity-30 disabled:cursor-not-allowed"
                          title="Move down"
                          data-testid={`social-down-${i}`}
                        >
                          <ArrowDown className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => removeLink(i)}
                          className="p-1.5 text-red-600 hover:bg-red-50"
                          title="Remove"
                          data-testid={`social-remove-${i}`}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </Card>
        )}

        {tab === "admin" && (
        <Card className="space-y-4">
          <h3 className="font-semibold">Change password</h3>
          <Field label="Current password"><Input type="password" value={pw.current_password} onChange={(e) => setPw((p) => ({ ...p, current_password: e.target.value }))} /></Field>
          <Field label="New password"><Input type="password" value={pw.new_password} onChange={(e) => setPw((p) => ({ ...p, new_password: e.target.value }))} /></Field>
          <PrimaryButton onClick={changePw}>Change password</PrimaryButton>
          <div className="text-xs text-[#6C757D] pt-2 border-t border-[#E9ECEF]">Default password is <code>admin123</code>. Change it now if you haven't already.</div>
        </Card>
        )}
      </div>
    </div>
  );
}

function SettingsTab({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-4 py-2 text-sm border-b-2 -mb-px ${active ? "border-[#212529] text-[#212529] font-medium" : "border-transparent text-[#6C757D] hover:text-[#212529]"}`}
    >
      {children}
    </button>
  );
}
